import { uniq, uniqWith } from 'es-toolkit';
import React from 'react';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PreviewIcon from '@mui/icons-material/Preview';
import { Box, Button, Divider, Stack } from '@mui/material';

import { INDETERMINATE, IndeterminateValue, isIndeterminate } from '@paragon/client-tools';
import { OrderedCuts, TopOrBottomCut, TrussEnvelope, TrussEnvelopeBevelCut } from '@paragon/contract-library';
import { angleEquals } from '@paragon/geometry-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField, NumericField, OptionalTooltip } from '@paragon/material';
import { notNullish } from '@paragon/utilities';

import { TransformationButtons } from '../../../../library/transformations/controls/TransformationButtons';
import {
  editExistingTrussEnvelopes,
  flipTrussEnvelopes,
  saveTrussEnvelopes,
  topOrBottomCutEquals,
} from '../../../../store/actions/layout/trussEnvelopeActions';
import { selectItemByGuid } from '../../../../store/actions/selectActions';
import { isLayoutBasedOnRoofContainers, isLayoutModeViewOnly } from '../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { openRenameLayoutComponentsPanel, rightToolBarShowSet } from '../../../../store/slices/panelDataSlice';
import { FeaturePatternContainerSelect } from '../../../controls/FeaturePatternContainerSelect';
import { RoofContainerSelect } from '../../../controls/RoofContainerSelect';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { EnvelopeJustificationToggle } from '../elements/EnvelopeJustificationToggle';
import { ExistingEnvelopeRecalculationControl } from '../elements/ExistingEnvelopeRecalculationControl';
import { FlipTrussEnvelopeButton } from '../insertions/truss-envelope/FlipTrussEnvelopeButton';
import { TrussDefinitionTypeSelect } from '../insertions/truss-envelope/TrussDefinitionTypeSelect';
import { TrussEnvelopeAdditionalCutsProperties } from '../insertions/truss-envelope/TrussEnvelopeAdditionalCutsProperties';
import { TrussTypeSelect } from '../insertions/truss-envelope/TrussTypeSelect';
import { CapSetProperties } from './CapSetProperties';
import { ProjectHierarchyContextSelects } from './ProjectHierarchyContextSelects';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { TrussEnvelopeBevelCutTypeSelect } from './TrussEnvelopeBevelCutTypeSelect';
import { TrussEnvelopeEndConnectionTypeSelect } from './TrussEnvelopeEndConnectionTypeSelect';
import { TrussEnvelopeLabelPlacementSelect } from './TrussEnvelopeLabelPlacementSelect';
import {
  determineBevelCutAndOverhangUiDisablement,
  LayoutComponentDesignNameField,
  MOVE_FEATURE_PATTERN_ENVELOPE_HELP_MSG,
} from './TrussEnvelopeProperties';

/*
  Note that we strongly desire to keep this panel as visually similar to the singular `TrussEnvelopeProperties` panel
    as possible. Please consider updating both panels when making any changes.
 */

export function BulkTrussEnvelopeProperties(props: { envelopes: TrussEnvelope[] }) {
  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const componentDesignGuids = uniq(props.envelopes.map((envelope) => envelope.componentDesignGuid).filter(notNullish));

  const levelGuids = uniq(props.envelopes.map((envelope) => envelope.levelGuid));
  const commonLevelGuid = levelGuids.length === 1 ? levelGuids[0] : INDETERMINATE;

  const componentTypes = uniq(props.envelopes.map((e) => e.componentType));
  const commonComponentType = componentTypes.length === 1 ? componentTypes[0] : INDETERMINATE;

  const definitionTypes = uniq(props.envelopes.map((e) => e.definitionType));
  const commonDefinitionType = definitionTypes.length === 1 ? definitionTypes[0] : INDETERMINATE;
  const disabledByDefinitionType = commonDefinitionType !== 'Profile';

  const inRoofContainerProject = useAppSelector(isLayoutBasedOnRoofContainers);

  const roofContainerGuids = uniq(props.envelopes.map((e) => e.roofContainerGuid).filter(notNullish));
  const commonRoofContainerGuid = roofContainerGuids.length === 1 ? roofContainerGuids[0] : INDETERMINATE;

  const featurePatternContainers = useAppSelector((state) => state.layout.featurePatternContainers); // TODO filter for active project hierarchy context?
  const featurePatternContainerGuids = uniq(
    props.envelopes.map((e) => e.featurePatternContainerGuid).filter(notNullish),
  );
  const commonFeaturePatternContainer =
    featurePatternContainerGuids.length === 1
      ? featurePatternContainers[featurePatternContainerGuids[0]]
      : INDETERMINATE;

  // a different check here than the `needsContainerSelection` used by similar panels, since we don't support
  //   bulk editing container associations
  const showAssociatedContainers =
    (commonDefinitionType === 'Profile' && inRoofContainerProject && roofContainerGuids.length > 0) ||
    (commonDefinitionType === 'FeaturePattern' && featurePatternContainerGuids.length > 0);

  const justifications = uniq(props.envelopes.map((e) => e.justification));

  const thicknesses = uniq(props.envelopes.map((e) => e.thickness));
  const commonThickness = thicknesses.length === 1 ? thicknesses[0] : INDETERMINATE;

  const plyCounts = uniq(props.envelopes.map((e) => e.numberOfPlies));
  const commonPlyCount = plyCounts.length === 1 ? plyCounts[0] : INDETERMINATE;

  const leftOverhangs = uniq(props.envelopes.map((e) => e.leftOverhang));
  const commonLeftOverhang = leftOverhangs.length === 1 ? leftOverhangs[0] : INDETERMINATE;

  const rightOverhangs = uniq(props.envelopes.map((e) => e.rightOverhang));
  const commonRightOverhang = rightOverhangs.length === 1 ? rightOverhangs[0] : INDETERMINATE;

  const elevations = uniq(props.envelopes.map((e) => e.elevation));
  const commonElevation = elevations.length === 1 ? elevations[0] : INDETERMINATE;

  const leftBevelCuts = uniqWith(
    props.envelopes.map((e) => e.leftBevelCut),
    bevelCutsAreLooselyEqual,
  );
  const commonLeftBevelCut = leftBevelCuts.length === 1 ? leftBevelCuts[0] : INDETERMINATE;

  const rightBevelCuts = uniqWith(
    props.envelopes.map((e) => e.rightBevelCut),
    bevelCutsAreLooselyEqual,
  );
  const commonRightBevelCut = rightBevelCuts.length === 1 ? rightBevelCuts[0] : INDETERMINATE;

  const leftEndConnectionOverride = uniq(props.envelopes.map((e) => e.leftEndConnectionOverride));
  const commonLeftEndConnectionOverride =
    leftEndConnectionOverride.length === 1 ? leftEndConnectionOverride[0] : INDETERMINATE;

  const rightEndConnectionOverride = uniq(props.envelopes.map((e) => e.rightEndConnectionOverride));
  const commonRightEndConnectionOverride =
    rightEndConnectionOverride.length === 1 ? rightEndConnectionOverride[0] : INDETERMINATE;

  const labelPlacements = uniq(props.envelopes.map((e) => e.labelPlacement));
  const commonLabelPlacement = labelPlacements.length === 1 ? labelPlacements[0] : INDETERMINATE;

  const [commonTopCuts, hiddenTopCutCount] = extractCommonCuts(props.envelopes.map((envelope) => envelope.topCuts));
  const [commonBottomCuts, hiddenBottomCutCount] = extractCommonCuts(
    props.envelopes.map((envelope) => envelope.bottomCuts),
  );

  const commonOrderedTopCuts = extractCommonOrderedCuts(props.envelopes.map((envelope) => envelope.orderedTopCuts));
  const commonOrderedBottomCuts = extractCommonOrderedCuts(
    props.envelopes.map((envelope) => envelope.orderedBottomCuts),
  );

  const disablement = determineBevelCutAndOverhangUiDisablement({
    leftOverhang: commonLeftOverhang,
    rightOverhang: commonRightOverhang,
    leftBevelCut: commonLeftBevelCut,
    rightBevelCut: commonRightBevelCut,
    multipleSelections: true,
  });

  return (
    <PropertiesPanel
      sx={{ gap: 2, marginTop: 2 }}
      title="Multiple Truss Envelopes"
      titleButton={<PropertiesPanelMenuButton type="trussEnvelope" guid={props.envelopes[0].guid} />}
    >
      {componentDesignGuids.length > 0 && (
        <BulkEnvelopesComponentDesignControls
          componentDesignGuids={componentDesignGuids}
          viewOnly={viewOnly}
          onUnlinkAll={() => {
            dispatch(
              saveTrussEnvelopes(props.envelopes.map((envelope) => ({ ...envelope, componentDesignGuid: null }))),
            );
          }}
        />
      )}

      <TransformationButtons disabled={viewOnly} />

      <Divider />

      <Stack direction="row" spacing={1}>
        <TrussTypeSelect
          disabled
          value={commonComponentType}
          onChange={async (componentType) => {
            await dispatch(
              editExistingTrussEnvelopes(
                props.envelopes.map((envelope) => ({
                  guid: envelope.guid,
                  editedProperties: { componentType },
                })),
              ),
            );
          }}
        />

        <TrussDefinitionTypeSelect
          disabled
          value={commonDefinitionType}
          trussType={commonComponentType}
          onChange={async (definitionType) => {
            await dispatch(
              editExistingTrussEnvelopes(
                props.envelopes.map((envelope) => ({
                  guid: envelope.guid,
                  editedProperties: { definitionType },
                })),
              ),
            );
          }}
        />
      </Stack>

      {!isIndeterminate(commonLevelGuid) && (
        <OptionalTooltip visible={disabledByDefinitionType} title={MOVE_FEATURE_PATTERN_ENVELOPE_HELP_MSG}>
          <ProjectHierarchyContextSelects
            levelGuid={commonLevelGuid}
            onChange={async (levelGuid) => {
              await dispatch(
                editExistingTrussEnvelopes(
                  props.envelopes.map((envelope) => ({
                    guid: envelope.guid,
                    editedProperties: { levelGuid },
                  })),
                ),
              );
            }}
            disabled={viewOnly || disabledByDefinitionType}
          />
        </OptionalTooltip>
      )}

      {showAssociatedContainers &&
        (commonDefinitionType === 'Profile' ? (
          <RoofContainerSelect
            disabled
            roofContainerGuid={commonRoofContainerGuid}
            onChange={async () => {
              // editing disabled due to potential to temporarily corrupt envelope
            }}
          />
        ) : commonDefinitionType === 'FeaturePattern' ? (
          <FeaturePatternContainerSelect
            sx={{ marginY: 0 }}
            disabled={viewOnly}
            featurePatternContainerGuid={
              isIndeterminate(commonFeaturePatternContainer)
                ? commonFeaturePatternContainer
                : commonFeaturePatternContainer!.guid
            }
            onChange={async (featurePatternContainerGuid) => {
              await dispatch(
                editExistingTrussEnvelopes(
                  props.envelopes.map((envelope) => ({
                    guid: envelope.guid,
                    editedProperties: { featurePatternContainerGuid },
                  })),
                ),
              );
            }}
            levelGuidFilter={commonLevelGuid}
          />
        ) : null)}

      <ExistingEnvelopeRecalculationControl envelopes={props.envelopes} disabled={viewOnly} />

      <Divider />

      <EnvelopeJustificationToggle
        value={justifications}
        onChange={async (justification) => {
          await dispatch(
            editExistingTrussEnvelopes(
              props.envelopes.map((envelope) => ({ guid: envelope.guid, editedProperties: { justification } })),
            ),
          );
        }}
        disabled={viewOnly}
      />

      <Stack direction="row" spacing={1} sx={{ paddingTop: 1 }}>
        <DistanceField
          sx={{ marginBottom: 0 }}
          label="Thickness"
          min={1 / 16}
          canBeIndeterminate
          value={commonThickness}
          onSubmit={async (thickness) =>
            await dispatch(
              editExistingTrussEnvelopes(
                props.envelopes.map((envelope) => ({ guid: envelope.guid, editedProperties: { thickness } })),
              ),
            )
          }
          disabled={viewOnly}
        />

        <NumericField
          sx={{ marginBottom: 0 }}
          label="Number of Plies"
          min={1}
          integers
          canBeIndeterminate
          value={commonPlyCount}
          onSubmit={async (numberOfPlies) =>
            await dispatch(
              editExistingTrussEnvelopes(
                props.envelopes.map((envelope) => ({ guid: envelope.guid, editedProperties: { numberOfPlies } })),
              ),
            )
          }
          disabled={viewOnly}
        />
      </Stack>

      <Stack direction="column" spacing={2}>
        <FlipTrussEnvelopeButton
          envelopes={props.envelopes}
          onClick={() => dispatch(flipTrussEnvelopes(props.envelopes))}
          disabled={viewOnly}
        />
      </Stack>

      <Divider />

      {commonComponentType !== 'FloorTruss' && (
        <div>
          <DistanceField
            sx={{ marginBottom: 0, marginTop: 0 }}
            label="Left Overhang"
            min={0}
            canBeIndeterminate
            value={commonLeftOverhang}
            onSubmit={async (leftOverhang) =>
              await dispatch(
                editExistingTrussEnvelopes(
                  props.envelopes.map((envelope) => ({ guid: envelope.guid, editedProperties: { leftOverhang } })),
                ),
              )
            }
            disabled={viewOnly || disablement.leftOverhang}
          />

          <DistanceField
            sx={{ marginBottom: 0 }}
            label="Right Overhang"
            min={0}
            canBeIndeterminate
            value={commonRightOverhang}
            onSubmit={async (rightOverhang) =>
              await dispatch(
                editExistingTrussEnvelopes(
                  props.envelopes.map((envelope) => ({ guid: envelope.guid, editedProperties: { rightOverhang } })),
                ),
              )
            }
            disabled={viewOnly || disablement.rightOverhang}
          />
        </div>
      )}

      <DistanceField
        sx={{ marginBottom: 0, marginTop: 0 }}
        label="Elevation"
        canBeIndeterminate
        value={commonElevation}
        onSubmit={async (elevation) =>
          await dispatch(
            editExistingTrussEnvelopes(
              props.envelopes.map((envelope) => ({ guid: envelope.guid, editedProperties: { elevation } })),
            ),
          )
        }
        disabled={viewOnly}
      />

      <TrussEnvelopeBevelCutTypeSelect
        name="Left Bevel Cut"
        bevelCut={commonLeftBevelCut}
        submit={async (leftBevelCut) => {
          await dispatch(
            editExistingTrussEnvelopes(
              props.envelopes.map((envelope) => ({
                guid: envelope.guid,
                bevelCuts: { leftBevelCut },
              })),
            ),
          );
        }}
        disabled={viewOnly || disablement.leftBevelCut}
      />

      <TrussEnvelopeBevelCutTypeSelect
        name="Right Bevel Cut"
        bevelCut={commonRightBevelCut}
        submit={async (rightBevelCut) => {
          await dispatch(
            editExistingTrussEnvelopes(
              props.envelopes.map((envelope) => ({
                guid: envelope.guid,
                bevelCuts: { rightBevelCut },
              })),
            ),
          );
        }}
        disabled={viewOnly || disablement.rightBevelCut}
      />

      <TrussEnvelopeEndConnectionTypeSelect
        name="Left Carried Chords"
        connectionType={commonLeftEndConnectionOverride}
        submit={async (leftEndConnectionOverride) =>
          await dispatch(
            editExistingTrussEnvelopes(
              props.envelopes.map((envelope) => ({
                guid: envelope.guid,
                editedProperties: { leftEndConnectionOverride },
              })),
            ),
          )
        }
        disabled={viewOnly}
      />

      <TrussEnvelopeEndConnectionTypeSelect
        name="Right Carried Chords"
        connectionType={commonRightEndConnectionOverride}
        submit={async (rightEndConnectionOverride) =>
          await dispatch(
            editExistingTrussEnvelopes(
              props.envelopes.map((envelope) => ({
                guid: envelope.guid,
                editedProperties: { rightEndConnectionOverride },
              })),
            ),
          )
        }
        disabled={viewOnly}
      />

      <TrussEnvelopeLabelPlacementSelect
        labelPlacement={commonLabelPlacement}
        submit={async (labelPlacement) =>
          await dispatch(
            editExistingTrussEnvelopes(
              props.envelopes.map((envelope) => ({
                guid: envelope.guid,
                editedProperties: { labelPlacement },
              })),
            ),
          )
        }
        disabled={viewOnly}
      />

      {commonDefinitionType === 'Profile' && (
        <>
          <CapSetProperties envelopes={props.envelopes} viewOnly={viewOnly} />

          <TrussEnvelopeAdditionalCutsProperties
            topCuts={commonTopCuts}
            hiddenTopCutCount={hiddenTopCutCount}
            bottomCuts={commonBottomCuts}
            hiddenBottomCutCount={hiddenBottomCutCount}
            orderedCutProps={{ orderedTopCuts: commonOrderedTopCuts, orderedBottomCuts: commonOrderedBottomCuts }}
            targetInfo={{ target: 'existingTrussEnvelopes', trussEnvelopeGuids: props.envelopes.map((e) => e.guid) }}
            disabled={viewOnly}
          />
        </>
      )}
    </PropertiesPanel>
  );
}

function BulkEnvelopesComponentDesignControls(props: {
  componentDesignGuids: string[];
  viewOnly?: boolean;
  onUnlinkAll: () => void;
}) {
  const dispatch = useAppDispatch();
  const { componentDesignGuids } = props;

  if (componentDesignGuids.length === 0) return null;

  return componentDesignGuids.length === 1 ? (
    <>
      <LayoutComponentDesignNameField componentDesignGuid={componentDesignGuids[0]} disabled={props.viewOnly} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          disabled={props.viewOnly}
          onClick={() => {
            dispatch(selectItemByGuid(componentDesignGuids[0], 'componentDesign', { intent: 'select' }));
            dispatch(rightToolBarShowSet(true));
          }}
        >
          Edit Component
        </Button>
        <Button variant="outlined" startIcon={<LinkOffIcon />} disabled={props.viewOnly} onClick={props.onUnlinkAll}>
          Unlink
        </Button>
      </Box>
    </>
  ) : (
    <Button
      variant="outlined"
      disabled={props.viewOnly}
      onClick={() => void dispatch(openRenameLayoutComponentsPanel({ componentDesignGuids }))}
    >
      Rename Component Designs
    </Button>
  );
}

function extractCommonCuts(sources: (readonly TopOrBottomCut[])[]): [TopOrBottomCut[], number] {
  const uniqueCuts = uniqWith(sources.flat(), topOrBottomCutEquals);
  const commonCuts = uniqueCuts.filter((uniqueCut) =>
    sources.every((source) => source.some((sourceCut) => topOrBottomCutEquals(uniqueCut, sourceCut))),
  );

  const otherCutCount = uniqueCuts.length - commonCuts.length;

  return [commonCuts, otherCutCount];
}

function extractCommonOrderedCuts(sources: OrderedCuts[]): OrderedCuts | IndeterminateValue {
  const uniqueOrientations = uniq(sources.map((source) => source.reversed));
  if (uniqueOrientations.length !== 1) return INDETERMINATE;

  const uniqueLengths = uniq(sources.map(({ cuts }) => cuts.length));
  if (uniqueLengths.length !== 1) return INDETERMINATE;
  const length = uniqueLengths[0];

  for (let i = 0; i < length; i++) {
    const uniqueCutsAtThisIndex = uniqWith(
      sources.map(({ cuts }) => cuts[i]),
      topOrBottomCutEquals,
    );
    if (uniqueCutsAtThisIndex.length !== 1) return INDETERMINATE;
  }

  return sources[0];
}

function bevelCutsAreLooselyEqual(a: TrussEnvelopeBevelCut | null, b: TrussEnvelopeBevelCut | null) {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;

  return a.type === b.type && angleEquals(a.angle, b.angle);
}
